import { apiClient } from '../config/api';

export interface Caretaker {
  id: string;
  firstName: string | null;
  lastName: string | null;
  avatar: string | null;
  specialties: {
    id: string;
    name: string;
    description: string | null;
  }[];
  availability: {
    dayOfWeek: string;
    startTime: string;
    endTime: string;
  }[];
  averageRating: number;
  reviewCount: number;
  createdAt: string;
}

export interface Specialty {
  id: string;
  name: string;
  description: string | null;
  _count: {
    caretakers: number;
  };
}

export interface SearchFilters {
  query?: string;
  specialty?: string;
  availability?: 'now' | 'this_week' | 'this_month';
  minRating?: number;
  services?: string[];
  /** FEAT-062 (MVP-18.4) — area-match filter; only sent when a valid 5-digit US zip is provided. */
  zip?: string;
}

export interface SearchResponse {
  success: boolean;
  data: Caretaker[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  timestamp: string;
}

export interface SpecialtiesResponse {
  success: boolean;
  data: Specialty[];
  timestamp: string;
}

class CaretakerSearchService {
  /**
   * Search for caretakers with filters
   */
  async searchCaretakers(
    filters: SearchFilters,
    page: number = 1,
    limit: number = 10
  ): Promise<SearchResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters.query) {
      params.append('q', filters.query);
    }
    if (filters.specialty) {
      params.append('specialty', filters.specialty);
    }
    if (filters.availability) {
      params.append('availability', filters.availability);
    }
    if (filters.minRating) {
      params.append('minRating', filters.minRating.toString());
    }
    if (filters.services && filters.services.length > 0) {
      filters.services.forEach(service => {
        params.append('services', service);
      });
    }
    if (filters.zip && /^\d{5}$/.test(filters.zip)) {
      params.append('zip', filters.zip);
    }

    const response = await apiClient.get<SearchResponse>(`/caretakers/search?${params.toString()}`);
    return response.data;
  }

  /**
   * Get all available specialties
   */
  async getSpecialties(): Promise<SpecialtiesResponse> {
    const response = await apiClient.get<SpecialtiesResponse>('/caretakers/specialties');
    return response.data;
  }

  /**
   * Get caretaker details by ID
   */
  async getCaretakerById(id: string): Promise<{ success: boolean; data: Caretaker }> {
    const response = await apiClient.get<{ success: boolean; data: Caretaker }>(`/caretakers/${id}`);
    return response.data;
  }

  /**
   * Get caretaker suggestions for search autocomplete
   */
  async getCaretakerSuggestions(query: string): Promise<Caretaker[]> {
    if (!query || query.length < 2) {
      return [];
    }

    try {
      const response = await this.searchCaretakers({ query }, 1, 5);
      return response.data;
    } catch (error) {
      console.error('Error fetching caretaker suggestions:', error);
      return [];
    }
  }

  /**
   * Get specialty suggestions for search autocomplete
   */
  async getSpecialtySuggestions(query: string): Promise<string[]> {
    if (!query || query.length < 2) {
      return [];
    }

    try {
      const specialties = await this.getSpecialties();
      const filteredSpecialties = specialties.data
        .filter(specialty => 
          specialty.name.toLowerCase().includes(query.toLowerCase())
        )
        .map(specialty => specialty.name)
        .slice(0, 5);
      
      return filteredSpecialties;
    } catch (error) {
      console.error('Error fetching specialty suggestions:', error);
      return [];
    }
  }
}

export const caretakerSearchService = new CaretakerSearchService();
